import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type DashRange, type DashboardSummary, type SalesReport, type StockReport } from '../api';
import { HBars, VBars } from '../components/Charts';

function money(v: number, currency: string): string {
  try {
    return new Intl.NumberFormat('fr-CH', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${Math.round(v).toLocaleString('fr-CH')} ${currency}`;
  }
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// "2026-07" -> "juil. 26" ; "2026-07-12" -> "12/07"
function bucketLabel(bucket: string, granularity: 'day' | 'month'): string {
  const [y, m, d] = bucket.split('-').map(Number);
  if (!y || !m) return bucket;
  if (granularity === 'day') return d ? `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}` : bucket;
  return `${new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'short' })} ${String(y).slice(2)}`;
}

function frDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d ? `${d}/${m}/${y}` : iso;
}

type Preset = 'month' | '3m' | '6m' | '12m' | 'year' | 'last_year' | 'custom';

const PRESETS: [Preset, string][] = [
  ['month', 'Ce mois'],
  ['3m', '3 mois'],
  ['6m', '6 mois'],
  ['12m', '12 mois'],
  ['year', 'Cette année'],
  ['last_year', 'Année dernière'],
  ['custom', 'Personnalisé'],
];

/**
 * Bornes d'un préréglage. Les « N mois » partent du 1er du mois concerné : sur
 * un CA mensuel, une fenêtre glissante à la journée coupe le premier mois en
 * deux et le rend incomparable aux suivants.
 */
function presetRange(p: Exclude<Preset, 'custom'>): DashRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = isoDate(new Date(Date.UTC(y, m, now.getDate())));
  const firstOf = (year: number, month: number) => isoDate(new Date(Date.UTC(year, month, 1)));
  switch (p) {
    case 'month': return { from: firstOf(y, m), to: today };
    case '3m': return { from: firstOf(y, m - 2), to: today };
    case '6m': return { from: firstOf(y, m - 5), to: today };
    case '12m': return { from: firstOf(y, m - 11), to: today };
    case 'year': return { from: firstOf(y, 0), to: today };
    case 'last_year': return { from: firstOf(y - 1, 0), to: isoDate(new Date(Date.UTC(y - 1, 11, 31))) };
  }
}

export default function Dashboard() {
  const [preset, setPreset] = useState<Preset>('year');
  const [custom, setCustom] = useState<DashRange>(() => presetRange('year'));

  const range = useMemo<DashRange>(
    () => (preset === 'custom' ? custom : presetRange(preset)),
    [preset, custom],
  );

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [sales, setSales] = useState<SalesReport | null>(null);
  const [stock, setStock] = useState<StockReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Sequential: Odoo (staging) rate-limits parallel JSON-RPC bursts.
      setSummary(await api.getDashboardSummary(range));
      setSales(await api.getSalesReport(range));
    } catch (e: any) {
      setError(e?.message ?? 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  // L'état du stock ne dépend pas de la période : inutile de le relire à chaque
  // changement de filtre (c'est la lecture la plus lourde de l'écran).
  const loadStock = useCallback(async () => {
    try {
      setStock(await api.getStockReport());
    } catch {
      /* les cartes stock restent en attente ; l'erreur des ventes suffit */
    }
  }, []);
  useEffect(() => { loadStock(); }, [loadStock]);

  // Les 7 préréglages ne tiennent pas dans la largeur d'un téléphone : la bande
  // défile, et la puce active pouvait être hors champ au chargement — on ne
  // voyait alors pas sur quelle période portaient les chiffres affichés.
  const activeChip = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeChip.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [preset]);

  const cur = summary?.currency ?? sales?.currency ?? 'CHF';
  const periodLabel = `${frDate(range.from)} → ${frDate(range.to)}`;

  return (
    <div className="dash">
      <div className="dash-period">
        <div className="suivi-filters">
          {PRESETS.map(([k, label]) => (
            <button
              key={k}
              ref={preset === k ? activeChip : undefined}
              className={`suivi-filter ${preset === k ? 'active' : ''}`}
              onClick={() => setPreset(k)}
            >{label}</button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="dash-dates">
            <label>
              Du <input type="date" value={custom.from} max={custom.to}
                onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
            </label>
            <label>
              au <input type="date" value={custom.to} min={custom.from}
                onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
            </label>
          </div>
        )}
        <button className="btn-secondary dash-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : '↻ Actualiser'}
        </button>
      </div>

      {error && <div className="dash-error">Erreur&nbsp;: {error}</div>}

      {/* KPI cards */}
      {summary && (
        <div className="kpi-grid">
          <KpiCard
            label="CA facturé"
            value={money(summary.sales.revenue, cur)}
            hint={`HT · ${periodLabel}`}
            accent
          />
          <KpiCard
            label="Nombre de ventes"
            value={String(summary.sales.invoices)}
            hint={summary.sales.refunds > 0 ? `factures · ${summary.sales.refunds} avoir(s) déduit(s)` : 'factures émises'}
            to="/ventes?tab=factures"
          />
          <KpiCard
            label="Panier moyen"
            value={money(summary.sales.avgBasket, cur)}
            hint="HT par facture"
          />
          <KpiCard label="Devis en attente" value={String(summary.pendingQuotes)} hint="à valider" to="/ventes?tab=devis" />
          <KpiCard
            label="Impayés clients"
            value={money(summary.receivables.total, cur)}
            hint={`${summary.receivables.count} facture(s) · toutes périodes`}
            danger={summary.receivables.total > 0}
            to="/ventes?tab=factures"
          />
          <KpiCard
            label="Produits sous seuil"
            value={String(summary.criticalProducts)}
            danger={summary.criticalProducts > 0}
            to="/stocks?statut=below_min"
          />
        </div>
      )}

      {/* Ventes */}
      <section className="dash-section">
        <h2>Ventes <span className="dash-section-sub">factures comptabilisées · {periodLabel}</span></h2>
        <div className="dash-cards">
          <div className="dash-card dash-card-wide">
            <div className="dash-card-title">
              Chiffre d'affaires facturé — {sales?.granularity === 'day' ? 'par jour' : 'par mois'}
            </div>
            {sales ? (
              <VBars
                items={sales.series.map((s) => ({ label: bucketLabel(s.bucket, sales.granularity), value: s.revenue }))}
                format={(v) => money(v, cur)}
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
          <div className="dash-card">
            <div className="dash-card-title">Marge (coût standard des produits)</div>
            {sales ? (
              sales.margin.coverage < 5 ? (
                <div className="chart-empty">
                  Coûts non renseignés dans Odoo : la marge n'est pas calculable.
                </div>
              ) : (
                <div className="margin-box">
                  <div className="margin-pct">{sales.margin.percent.toFixed(1)}%</div>
                  <div className="margin-total">{money(sales.margin.total, cur)}</div>
                  {sales.margin.coverage < 95 && (
                    <div className="margin-note">
                      Sur {sales.margin.coverage.toFixed(0)}&nbsp;% du CA seulement — le reste n'a pas de coût dans Odoo.
                    </div>
                  )}
                </div>
              )
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
          <div className="dash-card">
            <div className="dash-card-title">Top produits</div>
            {sales ? (
              <HBars
                items={sales.topProducts.map((p) => ({
                  label: p.name, value: p.revenue, sub: `· ${p.qty} u.`,
                  to: p.id ? `/stocks?produit=${p.id}` : undefined,
                }))}
                format={(v) => money(v, cur)}
                emptyLabel="Aucune facture sur la période."
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
          <div className="dash-card">
            <div className="dash-card-title">Top clients</div>
            {sales ? (
              <HBars
                items={sales.topCustomers.map((c) => ({ label: c.name, value: c.revenue }))}
                format={(v) => money(v, cur)}
                color="var(--color-accent)"
                emptyLabel="Aucune facture sur la période."
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
        </div>
      </section>

      {/* Stocks */}
      <section className="dash-section">
        <h2>Stocks <span className="dash-section-sub">les plus vendus d'abord</span></h2>
        <div className="dash-cards">
          <div className="dash-card">
            <div className="dash-card-title">
              Produits sous le minimum{stock ? ` (${stock.belowMin.length})` : ''}
              {' '}<Link className="link-btn" to="/stocks?statut=below_min">voir →</Link>
            </div>
            {stock ? (
              <HBars
                items={stock.belowMin.slice(0, 12).map((p) => ({
                  label: p.name,
                  value: p.onHand,
                  sub: `/ min ${p.min}${p.sold ? ` · ${p.sold} vendues` : ''}`,
                  to: p.id ? `/stocks?produit=${p.id}` : undefined,
                }))}
                format={(v) => `${v}`}
                color="var(--color-danger)"
                emptyLabel="Aucun produit sous son seuil de réappro."
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
          <div className="dash-card">
            <div className="dash-card-title">
              Ruptures (stock ≤ 0){stock ? ` — ${stock.outOfStockCount} au total` : ''}
              {' '}<Link className="link-btn" to="/stocks?statut=rupture">voir →</Link>
            </div>
            {stock ? (
              <HBars
                items={stock.outOfStock.slice(0, 12).map((p) => ({
                  label: p.name,
                  value: p.sold,
                  sub: p.sold ? 'u. vendues' : 'jamais vendu',
                  to: p.id ? `/stocks?produit=${p.id}` : undefined,
                }))}
                format={(v) => `${v}`}
                color="var(--color-danger)"
                emptyLabel="Aucune rupture."
              />
            ) : (
              <Placeholder loading={loading} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  accent,
  danger,
  to,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
  danger?: boolean;
  /** Écran qui détaille ce chiffre : la carte entière devient cliquable. */
  to?: string;
}) {
  const cls = `kpi-card${accent ? ' kpi-accent' : ''}${danger ? ' kpi-danger' : ''}${to ? ' kpi-link' : ''}`;
  const body = (
    <>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="kpi-hint">{hint}</div>}
    </>
  );
  return to ? <Link className={cls} to={to}>{body}</Link> : <div className={cls}>{body}</div>;
}

function Placeholder({ loading }: { loading: boolean }) {
  return <div className="chart-empty">{loading ? 'Chargement…' : '—'}</div>;
}
